<template>
	<div ref="root" class="grow flex flex-col flex-wrap h-full">
		<slot
			name="toolbar"
			v-bind="{ selectedRows, openCreateTab }"
		>
			<AppDataGridToolbar
				v-if="!hideToolbar"
				class="mb-.5"
				:resource-key="resourceKey"
				:resource-name="gridName"
				:show-create-button="!hideCreateButton"
				:show-delete-button="!hideDeleteButton"
				:disable-delete-button="selectedRows.length < 1 || $isAllowed(`delete ${camelCaseResourceKey}`) && !$isAllowed(`deleteBulk ${camelCaseResourceKey}`) && selectedRows.length != 1"
				@create="openCreateTab"
				@delete="customDeleteFunction?.() ?? openDeleteConfirmation(selectedRows)"
			>
				<slot
					name="toolbar-actions"
					v-bind="{ selectedRows, openCreateTab, gridInstanceId }"
				/>
			</AppDataGridToolbar>
		</slot>
		<AgGridVue
			class="ag-theme-alpine grow"
			row-model-type="serverSide"
			:grid-options="gridOptions"
			:getRowId="getRowId"
			v-bind="{
        paginationPageSize,
        cacheBlockSize,
        columnDefs,
        getContextMenuItems,
        paginationPageSizeSelector: [20, 50, 100, 200, 500, 1000],
        ...({...$attrs, ['style']: undefined, ['class']: undefined}),
      }"
			@filter-changed="onFilterChanged"
			@sort-changed="persistColumnState"
			@column-visible="persistColumnState"
			@column-moved="persistColumnState"
			@column-resized="persistColumnState"
			@row-double-clicked="onDoubleClick($event)"
			@selection-changed="
        defaultSelectionHandler();
        $emit('selected', $event);
      "
			@grid-ready="onGridReadyLocal"
			@column-everything-changed="onColumnsLoaded"
			@cell-value-changed="onCellValueChanged"
			@keydown="onKeyDown"
		/>
	</div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import {
	getDefaultGridPreset,
	getSingleGridColumnDefinition,
	globalAxios,
	QFood,
	removeEntities,
} from "@q-food/q-food";
import type {
	AgGridEvent,
	CellValueChangedEvent,
	ColDef,
	GridApi,
	GridOptions,
	GridReadyEvent,
	IRowNode,
	IServerSideGetRowsParams,
	MenuItemDef,
	NewColumnsLoadedEvent,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridVue } from "ag-grid-vue3";
import { fromPairs } from "lodash";
import AppDataGridStatusbar from "~/components/AppDataGridStatusbar.vue";
import { translate } from "~/plugins/I18n.ts";
import { useAppTabs } from "~/stores/useAppTabs";
import type { Pageable } from "~/types";
import { convertFilterModelToRSQLString } from "~/utils/Grids/GridRSQLBuilder";
import { useApiResources } from "~stores/ApiResources";
import { useUserSettingsStore } from "~stores/UserSettings";
import { columnTypes } from "~utils/Grids/ColumnTypes";
import { AG_GRID_LOCALE_NL } from "~utils/Grids/GridLocalisation";
import { processFilterModel } from "~utils/Grids/GridProcessFilter.ts";
import { refreshGridsOfResource } from "~utils/Grids/RefreshGrid.ts";
import AppDataGridBooleanCellRenderer from "./AppDataGridBooleanCellRenderer.vue";
import AppDataGridFilterPresetsToolPanel from "./AppDataGridFilterPresetsToolPanel.vue";
import { useUserStore } from "~stores/User.ts";
import { getFilterModel } from "~utils/Grids/FilterModelHelper.ts";

const { findByName } = useApiResources();
const { getGridColumnState, setGridColumnState } = useUserSettingsStore();
const { error, success, confirm } = useToast();

const props = withDefaults(
	defineProps<{
		resourceKey: string;
		resourceUrl: string;
		gridName: string;
		gridKey?: string; // Optional override if resource is used for multiple views which require distinct filters and preferences
		child?: boolean;
		pageSize?: number;
		hideToolbar?: boolean;
		hideCreateButton?: boolean;
		hideDeleteButton?: boolean;
		hideRefreshButton?: boolean;
		onDoubleClickGoToShowPage?: boolean;
		contextMenuItems?: (string | MenuItemDef)[];
		masterDetail?: boolean;
		preselectFirstRow?: boolean;
		filters?: string;
		rowSelection?: any;
		calculateTotals?: (criteria: string) => Promise<Record<string, any>>;
		onRowDoubleClicked?: (e: RowDoubleClickedEvent) => void;
		onGridReady?: (e: GridReadyEvent) => void;
		customDeleteFunction?: Promise<void>;
	}>(),
	{
		child: false,
		pageSize: 100,
		rowSelection: "multiRow",
		preselectFirstRow: false,
		onDoubleClickGoToShowPage: false,
	},
);

const gridKey = ref(props.gridKey ?? props.resourceKey);
const state = ref(undefined as string | undefined);
const { resourceKey, gridName, resourceUrl } = toRefs(props);

const gridApi = {
	_value: null as GridApi | null,
	get value() {
		if (this._value == null) {
			throw new Error("GridApi is not yet available");
		}

		return this._value;
	},
};

const columnDefs = ref<ColDef[]>([]);
const paginationPageSize = ref(props.pageSize);
const cacheBlockSize = ref(100);
const selectedRows = ref<any[]>([]);
const refreshing = ref(false);
const criteriaUsedForTotals = ref<string | null>(null);
const previousResourceUrl = ref<string | null>(null);
const { isAllowed } = useUserStore();

const defaultSelectionHandler = () => {
	return (selectedRows.value = gridApi.value.getSelectedRows() ?? []);
};

const emit = defineEmits(["columns-loaded", "selected", "onDelete", "filtersChanged"]);

const gridInstanceId = Math.random().toString(36);

const gridOptions = Object.assign({}, {
	theme: "legacy",
	localeText: AG_GRID_LOCALE_NL,
	components: {
		AppDataGridBooleanCellRenderer,
		AppDataGridStatusbar,
		AppDataGridFilterPresetsToolPanel,
	},
	...(props.rowSelection != "none" && {
		rowSelection: {
			mode: props.rowSelection,
			checkboxes: true,
			enableClickSelection: true,
			headerCheckbox: false,
		},
	}),
	pagination: true,
	masterDetail: props.masterDetail,
	columnTypes,
	statusBar: {
		statusPanels: [
			{
				key: "filterStatus",
				statusPanel: "AppDataGridStatusbar",
				align: "left",
				statusPanelParams: {
					onRefresh: () => {
						refresh(undefined, true);
					},
				},
			},
		],
	},
	sideBar: {
		toolPanels: [
			{
				id: "columns",
				labelDefault: "Columns",
				labelKey: "columns",
				iconKey: "columns",
				toolPanel: "agColumnsToolPanel",
				toolPanelParams: {
					suppressRowGroups: true,
					suppressValues: true,
					suppressPivots: true,
					suppressPivotMode: true,
				},
			},
			{
				id: "filters",
				labelDefault: "Filters",
				labelKey: "filters",
				iconKey: "filter",
				toolPanel: "agFiltersToolPanel",
			},
			{
				id: "filterPresets",
				labelDefault: "Profielen",
				labelKey: "filterPresets",
				iconKey: "menu",
				toolPanel: "AppDataGridFilterPresetsToolPanel",
				width: 400,
				toolPanelParams: {
					gridKey: gridKey.value,
				},
			},
		],
	},
} as GridOptions<T>);

const { execute } = useAsyncState(
	(params: URLSearchParams) => {
		const url = new URL(`${QFood.BASE_URL + resourceUrl.value}`);

		// If the url already contains criteria, we need to append the other search params
		let searchCriteria = url.searchParams.get("criteria");
		if (params.get("criteria") && searchCriteria) {
			searchCriteria = `${searchCriteria};${params.get("criteria")}`;
		} else if (params.get("criteria")) {
			searchCriteria = params.get("criteria");
		}

		// Append all params to the url
		params.forEach((value, key) => {
			url.searchParams.append(key, value);
		});
		url.searchParams.set("criteria", searchCriteria || "");

		return globalAxios
			.get(url.toString())
			.then((res) => res.data)
			.catch((err) => err);
	},
	undefined,
	{
		immediate: false,
		shallow: true,
	},
);
const resource = findByName(resourceKey.value);
const { push, currentRoute } = useRouter();

const root = useTemplateRef("root") as Ref<HTMLDivElement>;

onBeforeMount(async () => {
	if (!props.child && currentRoute.value.fullPath.includes("index")) {
	}

	try {
		state.value = await getDefaultGridPreset(gridKey.value);
	} catch (e) {
		// dan niet
	}
});

onActivated(() => {
	try {
		const firstSelectedNode = gridApi.value.getSelectedNodes()[0];
		if (firstSelectedNode) {
			gridApi.value.ensureNodeVisible(firstSelectedNode, "middle");
		}
	} catch {
		// do nothing
	}
});

watch([resourceKey, resourceUrl], (nv, ov) => {
	if (nv !== ov) {
		selectedRows.value = [];
		gridApi.value.refreshServerSide({ purge: true });
	}
});

const serverSideDatasource: GridOptions<Pageable>["serverSideDatasource"] = {
	async getRows(params: IServerSideGetRowsParams) {
		if (resourceUrl.value == null) {
			console.warn("No resourceUrl provided, skipping grid data fetch");
			return params.fail();
		}

		const rsqlQuery = convertFilterModelToRSQLString(params.request.filterModel, params.api.getColumnDefs());

		const pageSize = params.api.paginationGetPageSize();
		paginationPageSize.value = pageSize;
		cacheBlockSize.value = pageSize;

		const searchParams = new URLSearchParams({
			page: (params.request.startRow / params.api.paginationGetPageSize()).toString(),
			size: paginationPageSize.value.toString(),
			criteria: rsqlQuery,
		});

		params.request.sortModel.forEach((sort) => {
			searchParams.append("sort", `${sort.colId},${sort.sort}`);
		});

		if (
			rsqlQuery !== criteriaUsedForTotals.value ||
			resourceUrl.value !== previousResourceUrl.value ||
			refreshing.value
		) {
			generatePinnedBottomData(rsqlQuery);
			criteriaUsedForTotals.value = rsqlQuery;
			previousResourceUrl.value = rsqlQuery;
		}

		// const json = await execute(0, searchParams);
		const url = new URL(`${QFood.BASE_URL + resourceUrl.value}`);

		// If the url already contains criteria, we need to append the other search params
		let searchCriteria = url.searchParams.get("criteria");
		if (searchParams.get("criteria") && searchCriteria) {
			searchCriteria = `${searchCriteria};${searchParams.get("criteria")}`;
		} else if (searchParams.get("criteria")) {
			searchCriteria = searchParams.get("criteria");
		}

		// Append all params to the url
		searchParams.forEach((value, key) => {
			url.searchParams.append(key, value);
		});
		url.searchParams.set("criteria", searchCriteria || "");

		const json = await globalAxios
			.get(url.toString())
			.then((res) => res.data)
			.catch((err) => err);

		if (json) {
			if (json == "Invalid permissions") {
				params.success({
					rowData: [],
					rowCount: 0,
				});
				throw Error(json);
			}

			if (!("content" in json)) {
				throw Error("Grid response should be paginated!");
			}

			const data = json?.content;

			params.success({
				rowData: data,
				rowCount: json.page.totalElements,
			});

			// gridApi.value.dispatchEvent({ type: "storeRefreshed" });

			refreshing.value = false;

			if (props.preselectFirstRow && gridApi.value.getRenderedNodes().length > 0) {
				gridApi.value.deselectAll();
				gridApi.value.setNodesSelected({
					nodes: [gridApi.value.getRenderedNodes()[0]],
					newValue: true,
				});
			}
		} else {
			params.fail();
		}
	},
};

function getContextMenuItems(): (string | MenuItemDef)[] {
	const actions = [];
	if (!props.hideCreateButton) {
		actions.push({
			name: "Nieuwe regel",
			action: openCreateTab,
		});
	}
	actions.push(
		...[
			{
				name: "Celwaarde kopiëren",
				action({ value, node, api, column }) {
					try {
						navigator.clipboard.writeText(value);
					} catch (e) {
						console.log(e);
						error("Kopiëren is mislukt");
					}
					api.flashCells({ rowNodes: [node!], columns: [column!] });
				},
			},
			{
				name: "Filter op celwaarde",
				action(params) {
					if (params.column?.isFilterAllowed()) {
						params.api.setFilterModel({
							[params.column.getColId()]: {
								type: "equals",
								filter: params.value,
							},
						});
					}
				},
			},
			...(props.contextMenuItems ?? []),
			"csvExport",
			"excelExport",
		],
	);
	return actions;
}

function openCreateTab() {
	const resource = findByName(resourceKey.value);
	if (resource) {
		push("/" + resource.name + "/create");
	}
}

const camelCaseResourceKey = resourceKey.value
	.split("-")
	.map((word, index) => {
		if (index == 0) {
			return word;
		}

		return word[0].toUpperCase() + word.slice(1);
	})
	.join("");

function openDeleteConfirmation(rows: Record<string, any>) {
	for (const row of rows) {
		if ((!"id") in row) return;
	}

	const translatedText =
		selectedRows.value.length <= 1
			? translate("common.messages.Are you sure you want to delete this item?")
			: translate("common.messages.Are you sure you want to delete the {0} selected items?", [
					selectedRows.value.length.toString(),
				]);

	confirm(translatedText, {
		denyButtonText: translate("common.words.no"),
		confirmButtonText: translate("common.words.yes"),
	})
		.then(async (result) => {
			if (!result.isConfirmed) return;
			if (props.rowSelection == "singleRow" || !isAllowed(`deleteBulk ${camelCaseResourceKey}`)) {
				await globalAxios.delete(`${resourceUrl.value}/${rows[0].id}`);
			} else {
				await removeEntities(resourceKey.value, { ids: rows.map((row) => row.id) });
			}
			emit("onDelete");
			success(selectedRows.value.length <= 1 ? "Item is succesvol verwijderd!" : "Items zijn succesvol verwijderd!");
		})
		.catch((e) => {
			console.log(e);
			const errorMessage = e.message ?? "Er is iets misgegaan!";
			error(errorMessage);
		})
		.finally(() => {
			refresh();
			refreshGridsOfResource(resourceKey.value);
		});
}

function onDoubleClick(event: RowDoubleClickedEvent) {
	if (props.onRowDoubleClicked != undefined) {
		return props.onRowDoubleClicked(event);
	}

	const rowId = event.data.id;
	const resource = findByName(resourceKey.value);

	if (resource == undefined) return;
	if (!("update" in resource.actions)) return;

	if (!props.onDoubleClickGoToShowPage) {
		push(`/${resource.name}/update/${rowId}?resourceUrl=${resourceUrl?.value}`);
	} else {
		push(`/${resource.name}/show/?resourceId=${rowId}`);
	}
}

async function onGridReadyLocal(params: GridReadyEvent) {
	gridApi._value = params.api;
	params.api.setGridOption(
		"columnDefs",
		await loadColumnDefinition(resourceKey.value, props.rowSelection == "multiRow"),
	);
	await applyColumnState(gridKey.value, params.api);
	params.api.setGridOption("serverSideDatasource", serverSideDatasource!);

	if (state.value == undefined) {
		try {
			state.value = await getDefaultGridPreset(gridKey.value);
		} catch (e) {
			// dan niet
		}
	}

	const observer = new ResizeObserver(() => {
		gridApi.value.resetRowHeights();
		gridApi.value.redrawRows();
	});

	observer.observe(root.value);

	if (props.onGridReady != undefined) {
		props.onGridReady(params);
	}

	if (state.value != undefined) {
		params.api.applyColumnState({
			state: JSON.parse(state.value.columnState),
			applyOrder: true,
		});

		params.api.setFilterModel(processFilterModel(JSON.parse(state.value.filterState)));
	}

	if (props.filters != undefined) {
		params.api.setFilterModel(JSON.parse(props.filters));
	}
}

function onKeyDown(event: KeyboardEvent) {
	if (!["ArrowUp", "ArrowDown"].includes(event.key)) return;
	event.preventDefault();

	if (!gridApi.value.getFocusedCell()) return;
	const currentNodeIndex = gridApi.value.getFocusedCell().rowIndex;
	const nextNode = gridApi.value.getDisplayedRowAtIndex(currentNodeIndex);
	if (!nextNode) return;

	if (event.shiftKey && event.ctrlKey) {
		// TODO: go down or up and (de)select everything in between
	} else if (event.ctrlKey) {
		return;
	} else if (event.shiftKey) {
		if (nextNode.isSelected()) {
			const nodeIndexToDeselect = currentNodeIndex + (event.key === "ArrowUp" ? 1 : -1);
			gridApi.value.getDisplayedRowAtIndex(nodeIndexToDeselect).setSelected(false, false);
		} else {
			nextNode.setSelected(true, false);
		}
	} else {
		nextNode.setSelected(true, true);
	}
}

async function loadColumnDefinition(resourceKey: string, addCheckbox = true) {
	const columnDefinition = [] as ColDef[];

	// if (addCheckbox) {
	//   columnDefinition.push({ suppressColumnsToolPanel: true, suppressHeaderMenuButton: true, suppressHeaderContextMenu: true, headerCheckboxSelection:true, checkboxSelection: true, width: 40 });
	// }

	const newColumnDefs = await getSingleGridColumnDefinition(resourceKey);

	// @ts-ignore-next-line
	columnDefinition.push(...newColumnDefs.map((v) => v));

	if (props.masterDetail) {
		columnDefinition[0]["cellRenderer"] = "agGroupCellRenderer";
	}

	return columnDefinition;
}

async function applyColumnState(resourceKey: string, api: GridApi) {
	const columnState = await getGridColumnState(resourceKey);
	if (columnState) {
		api.applyColumnState({
			state: columnState,
			applyOrder: true,
		});
	} else {
		await setGridColumnState(resourceKey, gridApi.value.getColumnState());
	}
}

function onColumnsLoaded(e: NewColumnsLoadedEvent) {
	if (e.api.getColumnDefs()) {
		emit("columns-loaded", e);
	}
}

const controller = new AbortController();

async function generatePinnedBottomData(criteria: string) {
	const columnsToAggregate = [
		"availablePackageQuantity",
		"availableUnitQuantity",
		"availableWeight",
		"deliveredPackageQuantity",
		"deliveredUnitQuantity",
		"deliveredWeight",
		"packageQuantity",
		"unitQuantity",
		"weight",
		"totalValue",
		"totalPrice",
		"totalPriceBasedOnDelivery",
		"baseValue",
		"importCosts",
		"costs",
		"customsValue",
	] as const;

	let pinnedBottomRowData = {};

	if (props.calculateTotals == undefined) return;

	document.querySelector(".ag-root")?.classList?.add("grid-totals-loading");
	gridApi.value.setGridOption("pinnedBottomRowData", [Object.fromEntries(columnsToAggregate.map((k) => [k, 0]))]);

	pinnedBottomRowData = await props.calculateTotals(criteria);

	document.querySelector(".ag-root")?.classList?.remove("grid-totals-loading");

	if (Object.keys(pinnedBottomRowData).length > 0 && criteria == criteriaUsedForTotals.value) {
		gridApi.value.setGridOption("pinnedBottomRowData", [pinnedBottomRowData]);
	}
}

function onFilterChanged({ api }: AgGridEvent) {
	const filterStatusBar = api.getStatusPanel<InstanceType<typeof AppDataGridStatusbar> | undefined>("filterStatus");

	if (filterStatusBar) {
		filterStatusBar.setFiltersActive(api.isAnyFilterPresent());
	}
	const filterModel = JSON.parse(getFilterModel(api));
	emit("filtersChanged", {
		filterModel: filterModel,
		criteria: convertFilterModelToRSQLString(filterModel, api.getColumnDefs()),
	});
}

const persistColumnState = useDebounceFn(() => {
	setGridColumnState(gridKey.value, gridApi.value.getColumnState());
}, 100);

/**
 * Given an update to a cell, this function will push the changes to the backend and refresh the grid.
 */
const onCellValueChanged = async (event: CellValueChangedEvent) => {
	const { column, data, api, value } = event;
	const resource = findByName(resourceKey.value);
	if (resource == undefined) return;

	const formDataEndpoints = ["purchase-order-row", "sales-order-row"];

	// Make sure number values are processable by backend.
	let processedValue = value;
	if (column.getColDef().type == "NumberColumn" && typeof value === "string") {
		processedValue = value.replace(",", ".");
	}

	// Prepare payload for PUT endpoint.
	const originalData = (await globalAxios.get(`/api/application/forms/Edit${resource.camelCaseName}Form/${data.id}`))
		.data;

	const submitValues = Object.assign({}, fromPairs(originalData.map((it) => [it.name, it.value])), {
		[column.getColId()]: processedValue,
	});
	let submitData = null;

	if (formDataEndpoints.includes(resourceKey.value)) {
		submitData = new FormData();
		submitData.append("data", new Blob([JSON.stringify(submitValues)], { type: "application/json" }));
	} else {
		submitData = submitValues;
	}

	try {
		await globalAxios.put(`${resource.uri.toString()}/${data.id}`, submitData, {
			headers: {
				"Content-Type": undefined, // let Axios determine it
			},
		});
		success("Item is succesvol bijgewerkt!"); // Do not await!!
	} catch (e) {
		await error(e.message ?? "Er is iets misgegaan bij het updaten! Wijzigingen worden teruggedraaid.");
	} finally {
		refresh();
	}
};

function refresh(dataIds: string[] = [], purge = false) {
	const selectedNodeIds = gridApi.value.getSelectedNodes()?.map((node) => node.data.id) ?? [];
	const selectRow = () => {
		const nodes = [];
		const nodeIds = dataIds.length > 0 ? dataIds : selectedNodeIds;
		gridApi.value.forEachNode((node) => {
			if (!nodeIds.includes(node.data.id)) return;
			nodes.push(node);
		});
		if (selectedNodeIds.length > 0) {
			gridApi.value.deselectAll();
		}
		gridApi.value.setNodesSelected({ nodes, newValue: true });
		gridApi.value.removeEventListener("storeRefreshed", selectRow);
		if (nodes[0]) {
			gridApi.value.ensureNodeVisible(nodes[0], "middle");
		}
	};

	refreshing.value = true;
	gridApi.value.addEventListener("storeRefreshed", selectRow);
	gridApi.value.refreshServerSide({ purge: purge });
}

// window.addEventListener("food:grid:select-resource", (event) => {
//   if (!(event instanceof CustomEvent)) return;
//   if (event.detail.resource !== resourceKey.value) return;
//
//
// });

window.addEventListener("food:grid:refresh-resource", (event) => {
	if (!(event instanceof CustomEvent)) return;
	if (event.detail.resource !== resourceKey.value) return;

	if (event.detail.resourceId != undefined) {
		refresh([event.detail.resourceId]);
	} else {
		refresh();
	}
});

window.addEventListener("food:grid:select-and-refresh-grid-row", async (event) => {
	if (!(event instanceof CustomEvent)) return;
	if (event.detail.resource !== resourceKey.value) return;
	if (event.detail.gridInstanceId !== gridInstanceId) return;
	gridApi.value.deselectAll();
	refreshGridsOfResource(resourceKey.value);
	const selectFirstGridRow = () => {
		selectGridRow(event);

		gridApi.value.removeEventListener("storeRefreshed", selectFirstGridRow);
	};
	gridApi.value.addEventListener("storeRefreshed", selectFirstGridRow);
});

window.addEventListener("food:grid:select-grid-row", async (event) => {
	if (!(event instanceof CustomEvent)) return;
	if (event.detail.resource !== resourceKey.value) return;
	selectGridRow(event);
});

function selectGridRow(event: object) {
	let row = null;
	if (event.detail.rowId != null) {
		row = gridApi.value.getRowNode(event.detail.rowId);
		if (row == null) return;
	}

	gridApi.value.setNodesSelected({
		nodes: [row ?? gridApi.value.getRenderedNodes()[0]],
		newValue: true,
	});
}
</script>
<script lang="ts">
import {
	GridApi as localGridApi,
	IRowNode as localIRowNode,
} from "ag-grid-community";

export function updateRow(
	api: localGridApi,
	id: string,
	rowValue: string,
	rowKey: string,
) {
	const selectedRowIndex: number = api.getSelectedNodes()[0].rowIndex ?? 0;
	api?.forEachNode((rowNode: localIRowNode) => {
		if (rowNode.data.id == id) {
			const updated = rowNode.data;
			updated[rowKey] = rowValue;

			rowNode.updateData(updated);
		}
	});
	api.deselectAll();
	api.getDisplayedRowAtIndex(selectedRowIndex)?.setSelected(true);
}

function getRowId(params) {
	return params.data?.id;
}
</script>
